/**
 * TanStack Query hooks for the audit Layer-1 query + forensic export
 * endpoints. Backend: `apps/api/flow_api/routers/audit.py`.
 *
 * Layer 1 (`/audit/query`) never returns PHI; only `hasPatient: bool`.
 * Patient-level detail goes through a Layer 3 forensic export, gated
 * by two-person approval below.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/apiClient";

// ──────────────────────────────────────────────────────────────
// Layer 1 — audit index query
// ──────────────────────────────────────────────────────────────

export interface AuditEntry {
  id: string;
  occurredAt: string;
  actorType: string;
  actorUserId: string | null;
  actorAgentJobId: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  hasPatient: boolean;
  entityId: string | null;
  blobRefCount: number;
  details: Record<string, unknown>;
}

export interface AuditQueryParams {
  startDate: string;
  endDate: string;
  entityCode?: string;
  action?: string;
  actorUserId?: string;
  limit?: number;
}

export function useAuditQuery(params: AuditQueryParams | null) {
  return useQuery<{ items: AuditEntry[]; total: number }>({
    queryKey: ["audit", "query", params],
    queryFn: () => {
      if (!params) throw new Error("params required");
      const qs = new URLSearchParams({
        start_date: params.startDate,
        end_date: params.endDate,
      });
      if (params.entityCode) qs.set("entity_code", params.entityCode);
      if (params.action) qs.set("action", params.action);
      if (params.actorUserId) qs.set("actor_user_id", params.actorUserId);
      if (params.limit) qs.set("limit", String(params.limit));
      return apiFetch(`/audit/query?${qs.toString()}`);
    },
    enabled: params !== null,
  });
}

// ──────────────────────────────────────────────────────────────
// Layer 3 — forensic export (two-person approval)
// ──────────────────────────────────────────────────────────────

export type ForensicExportState =
  | "pending_approval"
  | "approved"
  | "complete"
  | "rejected"
  | "failed";

export interface ForensicExport {
  id: string;
  state: ForensicExportState;
  requestedBy: string;
  approvedBy: string | null;
  entityCode: string | null;
  startAt: string;
  endAt: string;
  reason: string;
  blobRef: string | null;
  signaturePresent: boolean;
  requestedAt: string;
  approvedAt: string | null;
  completedAt: string | null;
  failureReason: string | null;
}

export interface ForensicExportCreatePayload {
  start_date: string;
  end_date: string;
  entity_code?: string;
  reason: string;
}

const EXPORTS_KEY = ["audit", "exports"] as const;

export function useForensicExports(params?: { state?: ForensicExportState }) {
  return useQuery<{ items: ForensicExport[]; total: number }>({
    queryKey: [...EXPORTS_KEY, "list", params?.state ?? "all"],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (params?.state) qs.set("state", params.state);
      const tail = qs.toString() ? `?${qs.toString()}` : "";
      return apiFetch(`/audit/export${tail}`);
    },
  });
}

export function useForensicExport(id: string | null) {
  return useQuery<ForensicExport>({
    queryKey: [...EXPORTS_KEY, "single", id],
    queryFn: () => apiFetch(`/audit/export/${id}`),
    enabled: id !== null,
  });
}

export function useRequestForensicExport() {
  const qc = useQueryClient();
  return useMutation<
    { id: string; state: ForensicExportState; requiresApprovalFrom: string },
    Error,
    ForensicExportCreatePayload
  >({
    mutationFn: (payload) =>
      apiFetch(`/audit/export`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: EXPORTS_KEY });
    },
  });
}

export function useApproveForensicExport() {
  const qc = useQueryClient();
  return useMutation<ForensicExport, Error, { id: string; notes?: string }>({
    mutationFn: ({ id, notes }) =>
      apiFetch(`/audit/export/${id}/approve`, {
        method: "POST",
        body: JSON.stringify(notes ? { notes } : {}),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: EXPORTS_KEY });
      qc.invalidateQueries({ queryKey: [...EXPORTS_KEY, "single", vars.id] });
    },
  });
}

export function useRejectForensicExport() {
  const qc = useQueryClient();
  return useMutation<ForensicExport, Error, { id: string; notes?: string }>({
    mutationFn: ({ id, notes }) =>
      apiFetch(`/audit/export/${id}/reject`, {
        method: "POST",
        body: JSON.stringify(notes ? { notes } : {}),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: EXPORTS_KEY });
      qc.invalidateQueries({ queryKey: [...EXPORTS_KEY, "single", vars.id] });
    },
  });
}
